import * as React from "react";
import { connect } from "react-redux";
import { getCalendar, actionCreators } from "page/calendar/store";
import { drawBorder } from "../../../../svg/draw/draw-border";
import {
    BorderOptions,
    CalendarDrawerContent,
    CalendarState,
    DateMetaData,
    ImageDetails,
    SpiralItem,
} from "burde";
import { actionCreators as uiActionCreators } from "ui/store";
import { getDualPagePreviewContainer } from "svg/draw/helpers/preview-helper";
import { MonthDivider as MonthDividerType } from "burde";
import * as cn from "classnames/bind";
import * as styles from "./month-divider.css";
import { ApplicationState } from "store";
import { CalendarDimensions, Page } from "svg/constants";
import drawDynamicExtraPage from "../../../../svg/draw/draw-dynamic-extra-page";
import { useResources } from "contexts/resource-context";
import moment from "moment";
import { getBorderImage, getMetadata } from "burde-api-content/store";

const cx = cn.bind(styles);

type Props = {
    calendar: CalendarState;
    monthDivider: MonthDividerType | null;
    spiral: SpiralItem | null;
    borderImage: ImageDetails | null;
    metadata: DateMetaData[];
} & typeof actionCreators &
    typeof uiActionCreators;

const MonthDivider = ({
    calendar,
    monthDivider,
    spiral,
    borderImage,
    metadata,
}: Props) => {
    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendar.calendarSize,
    );

    const resources = useResources();

    if (calendarDimensions === undefined) return <></>;

    const drawMonthDivider = async (ref: HTMLDivElement | null) => {
        let dualPageSvgs = getDualPagePreviewContainer(ref, calendar, spiral);

        if (!dualPageSvgs || monthDivider === null) return;

        // Left page
        const leftOwnerPageSvg = monthDivider.endpoints.leftPage;

        dualPageSvgs.leftSvg
            .append("g")
            .attr("transform", `translate(-30, -30)`)
            .html(leftOwnerPageSvg !== undefined ? leftOwnerPageSvg : "");

        dualPageSvgs.leftSvg
            .select(".outerContent")
            .attr("height", calendarDimensions.contentHeightWithoutBleeds + 60)
            .attr("width", calendarDimensions.contentWidthWithoutBleeds + 60);

        let innerContentHeight = dualPageSvgs.leftSvg
            .select(".innerContent")
            .attr("data-height");
        let innerContentWidth = dualPageSvgs.leftSvg
            .select(".innerContent")
            .attr("data-width");

        dualPageSvgs.leftSvg
            .select(".innerContent")
            .attr("height", innerContentHeight)
            .attr("width", innerContentWidth);

        // Right page
        const rightOwnerPageSvg = monthDivider.endpoints.rightPage;

        dualPageSvgs.rightSvg
            .append("g")
            .attr("transform", `translate(-30, -30)`)
            .html(rightOwnerPageSvg !== undefined ? rightOwnerPageSvg : "");

        dualPageSvgs.rightSvg
            .select(".outerContent")
            .attr("height", calendarDimensions.contentHeightWithoutBleeds + 60)
            .attr("width", calendarDimensions.contentWidthWithoutBleeds + 60);

        innerContentHeight = dualPageSvgs.rightSvg
            .select(".innerContent")
            .attr("data-height");
        innerContentWidth = dualPageSvgs.rightSvg
            .select(".innerContent")
            .attr("data-width");

        dualPageSvgs.rightSvg
            .select(".innerContent")
            .attr("height", innerContentHeight)
            .attr("width", innerContentWidth);

        if (calendar.monthDivider !== null && calendar.monthDivider.showFrame) {
            const calendarDrawerContent: CalendarDrawerContent = {
                article: null,
                calendarId: null,
                layout: null,
                dayModules: [],
                weekModules: [],
                metadata: metadata,
                coverImage: null,
                backCoverImage: null,
                customCoverImage: null,
                customBackImage: null,
                borderImage: borderImage,
                extraPages: null,
                salesPages: null,
                customerInformation: null,
                ownerPage: null,
                monthDivider: monthDivider,
                ownerPageImageUrl: "",
                weekNumberPrefix: "",
                spiral: spiral,
                rubberband: null,
                calendarName: null,
                fonts: null,
            };

            drawBorder(
                dualPageSvgs.leftSvg,
                calendar.borderColor,
                Page.Odd,
                calendar.calendarSize,
                false,
                calendarDrawerContent,
            );

            drawBorder(
                dualPageSvgs.rightSvg,
                calendar.borderColor,
                Page.Even,
                calendar.calendarSize,
                false,
                calendarDrawerContent,
            );
        }

        // Dynamic dates
        if (monthDivider.dynamicDates === true) {
            drawDynamicExtraPage(
                dualPageSvgs.leftSvg,
                moment(calendar.startDate),
                metadata,
                calendar.culture,
                resources["Header_WeekNumberPrefix"],
            );

            drawDynamicExtraPage(
                dualPageSvgs.rightSvg,
                moment(calendar.startDate),
                metadata,
                calendar.culture,
                resources["Header_WeekNumberPrefix"],
            );
        }
    };

    const svgContainerCn = () =>
        cx({
            svgContainer: true,
            "svg-container": true,
        });

    const calendarContainerCn = () =>
        cx({
            calendarContainer: true,
        });

    return (
        <div key={"month-divider"} className={calendarContainerCn()}>
            <div
                className={svgContainerCn()}
                ref={(ref) => drawMonthDivider(ref)}
            />
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    calendar: getCalendar(state),
    borderImage: getBorderImage(state),
    metadata: getMetadata(state),
});

const mapDispatchToProps = {
    ...actionCreators,
    ...uiActionCreators,
};

export default connect(mapStateToProps, mapDispatchToProps)(MonthDivider);
